import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../services/api';
import { handleError } from '../services/errorHandler';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      token: null,
      userInfo: null,
      loading: false, // Initial loading state, global
      operationLoading: false, // loading state, local
      error: null,

      // Actions
      login: async (username, password, captchaToken) => {
        set({ loading: true, error: null });
        try {
          const response = await authApi.login(username, password, captchaToken);
          const { token: access_token } = response;

          // Fetch user info after successful login
          const userInfoResponse = await authApi.getUserInfo(access_token);

          set({
            isAuthenticated: true,
            token: access_token,
            userInfo: userInfoResponse,
            loading: false,
            error: null,
          });

          // Note: persist middleware handles localStorage automatically
          // This is redundant but kept for backward compatibility
          localStorage.setItem('token', access_token);
          return { access_token, user: userInfoResponse };
        } catch (error) {
          const errorInfo = handleError(error);
          set({
            error: errorInfo.message,
            loading: false,
            isAuthenticated: false,
            token: null,
            userInfo: null,
          });
          // Attach error info for component to use
          error.errorInfo = errorInfo;
          throw error;
        }
      },

      register: async (userData) => {
        set({ loading: true, error: null });
        try {
          const res = await authApi.register(
            userData.username,
            userData.email,
            userData.password,
            userData.captcha_token
          );

          if (res.status === 201) {
                set({ loading: false });
                return { success: true, message: 'Please check your email to verify your account.' };
              } else {
                throw new Error('Unexpected response from server');
              }
        } catch (error) {
          const errorMessage =
            error.response?.data?.error ||
            error.response?.data?.detail ||
            'Registration failed';
          set({ error: errorMessage, loading: false });
          const err = new Error(errorMessage);
          err.success = false;
          throw err;
        }
      },

      loginByShortLivedAuthCode: async (slaCode) => {
        try {
          const response = await authApi.loginByShortLivedAuthCode(slaCode);
          const { access_token, userInfo } = response;

          set({
            isAuthenticated: true,
            token: access_token,
            userInfo: userInfo,
            loading: false,
            error: null,
          });

          localStorage.setItem('token', access_token);
          //localStorage.setItem('refresh_token', refresh);

          return { success: true, ...response };
        } catch (error) {
          console.error("[AuthStore] loginByShortLivedAuthCode error", error);
          const errorMessage = error.response?.data?.detail ||
                               error.response?.data?.error ||
                               'Login by short lived auth code is failed';
          set({ error: errorMessage, loading: false });
          return { success: false, error: errorMessage };
        }
      },

      verifyEmail: async (activationToken) => {
        try {
          const response = await authApi.verifyEmail(activationToken);
          const { access_token, userInfo } = response;

          set({
            isAuthenticated: true,
            token: access_token,
            userInfo: userInfo,
            loading: false,
            error: null,
          });

          localStorage.setItem('token', access_token);
          //localStorage.setItem('refresh_token', refresh);

          return { success: true, ...response };
        } catch (error) {
          console.error("[AuthStore] verifyEmail error", error);
          const errorMessage = error.response?.data?.detail ||
                               error.response?.data?.error ||
                               'Email verification failed';
          set({ error: errorMessage, loading: false });
          return { success: false, error: errorMessage };
        }
      },

      resetPasswordWithToken: async (token, newPassword) => {
        set({ operationLoading: true, error: null });
        try {
          const res = await authApi.resetPasswordWithToken(token, newPassword);
          const { access_token, userInfo, message } = res;

          if (access_token && userInfo) {
            set({
              isAuthenticated: true,
              token: access_token,
              userInfo,
              operationLoading: false,
              error: null,
            });
            localStorage.setItem('token', access_token);
          } else {
            set({ operationLoading: false });
          }

          return { success: true, message };
        } catch (error) {
          const errorMessage =
            error.response?.data?.error ||
            error.response?.data?.detail ||
            'auth.reset_password.failed';

          set({ error: errorMessage, operationLoading: false });
          return { success: false, error: errorMessage };
        }
      },

      logout: async () => {
        const { token } = get();
        set({ loading: true });

        try {
          if (token) {
            // Try to logout on server, but don't fail if endpoint doesn't exist
            try {
              await authApi.logout(token);
            } catch (err) {
              // Silent fail - logout endpoint might not exist on backend
            }
          }
        } catch (error) {
          // Logout error - silent fail
        } finally {
          set({
            isAuthenticated: false,
            token: null,
            userInfo: null,
            loading: false,
            error: null,
          });
          localStorage.removeItem('token');
        }
      },

      refreshToken: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await authApi.refreshToken(token);
          const { access_token } = response;

          set({ token: access_token });
          localStorage.setItem('token', access_token);
          return access_token;
        } catch (error) {
          get().logout();
          throw error;
        }
      },

      updateUserInfo: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await authApi.getUserInfo(token);
          set({
            userInfo: response,
            isAuthenticated: true
          });
        } catch (error) {
          // If user info fetch fails, logout
          get().logout();
        }
      },

      setToken: (token) => {
        set({
          token,
          isAuthenticated: !!token
        });
        if (token) {
          localStorage.setItem('token', token);
        } else {
          localStorage.removeItem('token');
        }
      },

      setUserInfo: (userInfo) => set({ userInfo }),

      clearError: () => set({ error: null }),

      // Initialize from localStorage
      initialize: () => {
        const token = localStorage.getItem('token');
        if (token) {
          set({
            token,
            isAuthenticated: true
          });
          // Optionally fetch user info here
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        userInfo: state.userInfo,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
